import { Box, Typography } from '@mui/material'
import type { ReactNode } from 'react'

export type EmptyStateProps = {
  /** A MUI icon element; rendered at 24px inside a soft neutral disc. */
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Usually one button — the thing to do next. */
  action?: ReactNode
  'data-testid'?: string
}

/**
 * The one "nothing here yet" block. Empty screens used four different
 * treatments: react-admin's 160px gray inbox glyph with a 34px gray headline
 * (EMRs), a bare line of centered text (Clinic Team), a 48px share icon (Pet
 * Shares), and a blue info Alert (What's new, Group requests). An empty list
 * is a neutral state (UI-STYLE-GUIDE §3) — it gets a quiet icon, one line of
 * explanation, and the next action; never an info-blue banner.
 */
export const EmptyState = ({
  icon,
  title,
  description,
  action,
  'data-testid': testId,
}: EmptyStateProps) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      px: 3,
      py: { xs: 5, sm: 6 },
    }}
  >
    {icon && (
      <Box
        aria-hidden
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#f3f4f6',
          color: '#6b7280',
          mb: 2,
          '& .MuiSvgIcon-root': { fontSize: 24 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography variant="subtitle1" component="p" sx={{ color: '#111827', fontWeight: 600 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 440 }}>
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
)

export default EmptyState
